import { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getExpandedPlusSizeFilters } from '~/shared/utils/plusSizeFilters';

import getCioLeanFacetResults from '../api/getCioLeanFacetResults';
import { LEAN_FACET_STALE_TIME } from '../constants';

import useBaseFacets from './useBaseFacets';

// See docs/grid-provider-architecture.md "Staged Filters & Disabled Option States"
// for the full design rationale on debouncing, stale data handling, and why
// keepPreviousData doesn't work for this use case.
const LEAN_FACET_DEBOUNCE_MS = 300;

interface UseLeanFacetQueryParams {
  stagedFilters: Record<string, string[]>;
}

const useLeanFacetQuery = ({ stagedFilters }: UseLeanFacetQueryParams) => {
  const {
    baseFacets,
    baseTotalNumResults,
    isBaseFacetsLoading,
    sharedParams,
    cioClient,
    isCio,
  } = useBaseFacets();

  // Debounce staged filters so rapid clicks don't fire intermediate queries.
  // Only the final combination after the user stops clicking fires a request.
  const [debouncedStagedFilters, setDebouncedStagedFilters] =
    useState(stagedFilters);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedStagedFilters(stagedFilters);
    }, LEAN_FACET_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [stagedFilters]);

  // Dynamic facet query — fires with debounced staged filters when panel is open
  const expandedStagedFilters = useMemo(
    () => getExpandedPlusSizeFilters(debouncedStagedFilters),
    [debouncedStagedFilters]
  );

  const hasStagedFilters = useMemo(
    () =>
      Object.values(debouncedStagedFilters).some(
        (values) => values && values.length > 0
      ),
    [debouncedStagedFilters]
  );

  const { data: dynamicFacetsData, isFetching: isDynamicFacetsFetching } =
    useQuery({
      queryKey: ['LeanFacets:dynamic', sharedParams, expandedStagedFilters],
      queryFn: () =>
        getCioLeanFacetResults({
          cioClient,
          ...sharedParams,
          selectedUrlFilters: expandedStagedFilters,
        }),
      enabled: Boolean(cioClient) && isCio && hasStagedFilters,
      staleTime: LEAN_FACET_STALE_TIME,
    });

  // Freeze the last fresh result through loading so disabled states hold steady.
  // We intentionally avoid keepPreviousData here — it serves cached data from
  // ANY previous query key, which causes stale disabled states to flash when the
  // user clears filters and selects a different filter type. The ref only updates
  // when data is fresh for the current combination, and clears when filters are
  // empty. See docs/grid-provider-architecture.md "Why not keepPreviousData".
  const lastFreshDataRef = useRef<{
    facets: any[];
    totalNumResults: number;
  } | null>(null);

  if (hasStagedFilters && dynamicFacetsData && !isDynamicFacetsFetching) {
    lastFreshDataRef.current = {
      facets: dynamicFacetsData.facets,
      totalNumResults: dynamicFacetsData.totalNumResults,
    };
  } else if (!hasStagedFilters) {
    lastFreshDataRef.current = null;
  }

  // The query is "settling" when staged filters have changed but debounce
  // hasn't flushed yet, OR when the debounced query is still in-flight.
  const isSettling = stagedFilters !== debouncedStagedFilters;
  const isDynamicQueryLoading =
    isSettling || (hasStagedFilters && isDynamicFacetsFetching);

  return {
    baseFacets,
    baseTotalNumResults,
    dynamicFacets: lastFreshDataRef.current?.facets ?? null,
    dynamicTotalNumResults: lastFreshDataRef.current?.totalNumResults ?? null,
    isBaseFacetsLoading,
    isDynamicQueryLoading,
  };
};

export default useLeanFacetQuery;
